import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import type { CatalogIndex } from "@itaas/pep-contracts";
import { getProducts, getSolutions } from "../api/pep-api";
import { ProductMiniCard } from "../components/catalog/ProductCard";
import { SiteLayout } from "../components/layout/SiteLayout";
import { SeoHead } from "../components/seo/SeoHead";
import { PepApiErrorBanner } from "../components/feedback/PepApiErrorBanner";
import { DomainIcon, type DomainIconName } from "../home/HomeIcons";
import { usePepFetch } from "../hooks/usePepFetch";
import { usePepI18n } from "../i18n/usePepI18n";
import { SolutionsScrollRail } from "../solutions/SolutionsScrollRail";
import {
  SOLUTIONS_NEEDS,
  SOLUTIONS_PUBLISHABLE_PRODUCT_SLUGS,
  getJourneyEditorial,
  getSolutionsByTier,
  solutionsCopy,
  type SolutionsNeedId,
  type SolutionsProductEditorial,
  type SolutionsProductSlug,
} from "../solutions/solutions-content";

type CatalogProduct = CatalogIndex["products"][number];

const NEED_ICON: Record<SolutionsNeedId, DomainIconName> = {
  education: "education",
  hr: "hr",
  finance: "finance",
  operations: "ops",
  industry: "mines",
};

const PRODUCT_ICON: Record<SolutionsProductSlug, DomainIconName> = {
  edu: "education",
  rh: "hr",
  mineportal: "mines",
  inventory: "ops",
  erp: "erp",
  billing: "finance",
  hospitol: "health",
  security: "security",
};

function productLabel(p: CatalogProduct | undefined, editorial: SolutionsProductEditorial, en: boolean) {
  const fallback = en ? editorial.commercialNameEn : editorial.commercialNameFr;
  if (!p) return fallback;
  const fromCatalog = en
    ? p.displayNameEn ?? p.displayName ?? p.displayNameFr
    : p.displayNameFr ?? p.displayName ?? p.displayNameEn;
  if (!fromCatalog) return fallback;
  // Never surface catalog slug as a commercial name
  if (fromCatalog.trim().toLowerCase() === editorial.slug.toLowerCase()) return fallback;
  // Prefer Owner commercial short name when catalog is a longer variant of the same brand
  if (
    editorial.slug === "inventory" ||
    editorial.slug === "mineportal" ||
    editorial.slug === "billing"
  ) {
    return fallback;
  }
  return fromCatalog;
}

/**
 * Commercial surface: catalog proves existence / limited status.
 * Visible promise & capacities stay editorial (truth ≠ raw catalog copy).
 */
function productPromise(
  _p: CatalogProduct | undefined,
  editorial: SolutionsProductEditorial,
  en: boolean,
) {
  return en ? editorial.promiseEn : editorial.promiseFr;
}

function productCapacities(
  _p: CatalogProduct | undefined,
  editorial: SolutionsProductEditorial,
  en: boolean,
): string[] {
  return [...(en ? editorial.capacitiesEn : editorial.capacitiesFr)];
}

function TrustIcon({ id }: { id: string }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    "aria-hidden": true as const,
  };
  if (id === "rdc") {
    return (
      <svg {...common}>
        <path d="M12 21s7-4.4 7-11a7 7 0 1 0-14 0c0 6.6 7 11 7 11Z" />
        <circle cx="12" cy="10" r="2.2" />
      </svg>
    );
  }
  if (id === "host" || id === "lubum") {
    return (
      <svg {...common}>
        <path d="M4 20h16" />
        <path d="M6 20V9l6-4 6 4v11" />
        <path d="M10 20v-5h4v5" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

function NeedCard({
  need,
  exploreLabel,
  locale,
}: {
  need: (typeof SOLUTIONS_NEEDS)[number];
  exploreLabel: string;
  locale: "fr" | "en";
}) {
  const en = locale === "en";
  return (
    <article className="pep-sol-need">
      <div className="pep-sol-need__body">
        <span className="pep-sol-need__icon" aria-hidden="true">
          <DomainIcon name={NEED_ICON[need.id]} />
        </span>
        <h3 className="pep-sol-need__title">{en ? need.titleEn : need.titleFr}</h3>
        <p className="pep-sol-need__text">{en ? need.bodyEn : need.bodyFr}</p>
        <Link className="pep-sol-need__cta" to={need.journeyHref}>
          {exploreLabel} →
        </Link>
      </div>
      <div className="pep-sol-need__media">
        <img
          src={need.imageSrc}
          alt={en ? need.imageAltEn : need.imageAltFr}
          width={480}
          height={360}
          loading="lazy"
          decoding="async"
        />
      </div>
    </article>
  );
}

function ProductShowcaseCard({
  editorial,
  product,
  locale,
  badge,
  discoverLabel,
}: {
  editorial: SolutionsProductEditorial;
  product: CatalogProduct | undefined;
  locale: "fr" | "en";
  badge: string;
  discoverLabel: string;
}) {
  const en = locale === "en";
  const capacities = productCapacities(product, editorial, en);
  return (
    <article className={`pep-sol-product pep-sol-product--${editorial.tone} pep-sol-product--${editorial.tier}`}>
      <div className="pep-sol-product__bg" aria-hidden="true">
        <img src={editorial.imageSrc} alt="" loading="lazy" decoding="async" />
      </div>
      <div className="pep-sol-product__content">
        <div className="pep-sol-product__top">
          <span className="pep-sol-product__icon" aria-hidden="true">
            <DomainIcon name={PRODUCT_ICON[editorial.slug]} />
          </span>
          <span className={`pep-sol-badge pep-sol-badge--${editorial.tier}`}>{badge}</span>
        </div>
        <h3 className="pep-sol-product__name">{productLabel(product, editorial, en)}</h3>
        <p className="pep-sol-product__promise">{productPromise(product, editorial, en)}</p>
        <ul className="pep-sol-product__list">
          {capacities.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <Link className="pep-sol-product__cta" to={`/products/${editorial.slug}`}>
          {discoverLabel} →
        </Link>
      </div>
    </article>
  );
}

function LimitedCard({
  editorial,
  product,
  locale,
  badge,
  discoverLabel,
}: {
  editorial: SolutionsProductEditorial;
  product: CatalogProduct | undefined;
  locale: "fr" | "en";
  badge: string;
  discoverLabel: string;
}) {
  const en = locale === "en";
  const capacities = productCapacities(product, editorial, en);
  /** Never echo catalog availabilityNote here — trial/pilot claims often contradict portfolio-policy. */
  return (
    <article className="pep-sol-limited">
      <div className="pep-sol-limited__copy">
        <div className="pep-sol-limited__head">
          <span className="pep-sol-limited__icon" aria-hidden="true">
            <DomainIcon name={PRODUCT_ICON[editorial.slug]} />
          </span>
          <span className="pep-sol-badge pep-sol-badge--limited">{badge}</span>
        </div>
        <h3 className="pep-sol-limited__name">{productLabel(product, editorial, en)}</h3>
        <p className="pep-sol-limited__promise">{productPromise(product, editorial, en)}</p>
        <ul className="pep-sol-limited__list">
          {capacities.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <Link className="pep-sol-limited__cta" to={`/products/${editorial.slug}`}>
          {discoverLabel} →
        </Link>
      </div>
      <div className="pep-sol-limited__media">
        <img
          src={editorial.imageSrc}
          alt={en ? editorial.imageAltEn : editorial.imageAltFr}
          width={420}
          height={280}
          loading="lazy"
          decoding="async"
        />
      </div>
    </article>
  );
}

function PlatformDiagram({
  center,
  nodes,
}: {
  center: string;
  nodes: readonly string[];
}) {
  return (
    <div className="pep-sol-platform-diagram" aria-hidden="true">
      <div className="pep-sol-platform-diagram__ring">
        {nodes.map((n, i) => (
          <span key={n} className={`pep-sol-platform-diagram__node pep-sol-platform-diagram__node--${i}`}>
            {n}
          </span>
        ))}
      </div>
      <div className="pep-sol-platform-diagram__core">{center}</div>
    </div>
  );
}

export function SolutionsPage() {
  const { locale } = usePepI18n();
  const copy = solutionsCopy(locale);
  const en = locale === "en";
  const { data: products, loading, error, reload } = usePepFetch(getProducts);

  const bySlug = useMemo(() => {
    const map = new Map<string, CatalogProduct>();
    for (const p of products ?? []) map.set(p.slug, p);
    return map;
  }, [products]);

  const flagship = getSolutionsByTier("flagship");
  const standard = getSolutionsByTier("standard");
  const limited = getSolutionsByTier("limited");

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://www.itaas-suite.com/solutions#webpage",
        name: copy.seoTitle,
        description: copy.seoDescription,
        url: "https://www.itaas-suite.com/solutions",
        inLanguage: locale === "en" ? "en" : "fr",
        isPartOf: { "@type": "WebSite", name: "ITaaS Suite", url: "https://www.itaas-suite.com/" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "ITaaS Suite",
            item: "https://www.itaas-suite.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: copy.breadcrumbSolutions,
            item: "https://www.itaas-suite.com/solutions",
          },
        ],
      },
    ],
  };

  return (
    <SiteLayout>
      <SeoHead
        title={copy.seoTitle}
        description={copy.seoDescription}
        keywords={[...copy.seoKeywords]}
        canonicalPath="/solutions"
        locale={locale}
        jsonLd={jsonLd}
      />

      <section className="pep-sol-hero" data-section="S-HERO" aria-labelledby="solutions-h1">
        <div className="container pep-sol-hero__grid">
          <div className="pep-sol-hero__copy">
            <h1 id="solutions-h1" className="pep-sol-hero__title">
              {copy.heroH1Before}
              <span className="pep-sol-hero__emphasis">{copy.heroH1Emphasis}</span>
            </h1>
            <p className="pep-sol-hero__lead">{copy.heroLead}</p>
            <div className="pep-sol-hero__actions">
              <Link className="btn pep-sol-btn-primary" to={copy.primaryHref}>
                {copy.primaryCta} →
              </Link>
              <Link className="btn pep-sol-btn-secondary" to={copy.secondaryHref}>
                {copy.secondaryCta} →
              </Link>
            </div>
            <ul className="pep-sol-trust list-unstyled">
              {copy.trust.map((t) => (
                <li key={t.id}>
                  <TrustIcon id={t.id} />
                  <span>{t.label}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="pep-sol-hero__visual" aria-label={en ? "Business domains" : "Univers métiers"}>
            <div className="pep-sol-hero-fan">
              {SOLUTIONS_NEEDS.map((need) => (
                <figure key={need.id} className={`pep-sol-hero-fan__panel pep-sol-hero-fan__panel--${need.id}`}>
                  <img
                    src={need.imageSrc}
                    alt={en ? need.imageAltEn : need.imageAltFr}
                    width={320}
                    height={480}
                    decoding="async"
                    fetchPriority={need.id === "education" || need.id === "industry" ? "high" : "low"}
                  />
                  <figcaption>
                    <strong>{en ? need.heroLabelEn : need.heroLabelFr}</strong>
                    <span>{en ? need.heroCaptionEn : need.heroCaptionFr}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="pep-sol-needs" data-section="S-NEEDS" aria-labelledby="solutions-needs-h2">
        <div className="container">
          <div className="pep-sol-section-head">
            <div>
              <h2 id="solutions-needs-h2" className="pep-sol-section-title">
                {en ? (
                  <>
                    Which <span className="pep-sol-hero__emphasis">{copy.needsTitleEmphasis}</span> do you want
                    to solve today?
                  </>
                ) : (
                  <>
                    Quel <span className="pep-sol-hero__emphasis">{copy.needsTitleEmphasis}</span> souhaitez-vous
                    résoudre aujourd’hui ?
                  </>
                )}
              </h2>
              <p className="pep-sol-section-lead">{copy.needsLead}</p>
            </div>
            <a className="pep-sol-section-link" href="#solutions-journeys">
              {copy.needsAllJourneys} →
            </a>
          </div>

          <div className="pep-sol-needs__desktop">
            <div className="pep-sol-needs__grid">
              {SOLUTIONS_NEEDS.map((need) => (
                <NeedCard key={need.id} need={need} exploreLabel={copy.needsExplore} locale={locale} />
              ))}
            </div>
          </div>

          <div className="pep-sol-needs__mobile">
            <SolutionsScrollRail
              label={
                en
                  ? "Business challenges carousel. Use arrows or swipe."
                  : "Carrousel des défis métiers. Utilisez les flèches ou glissez."
              }
              prevLabel={copy.carouselPrev}
              nextLabel={copy.carouselNext}
            >
              {SOLUTIONS_NEEDS.map((need) => (
                <div key={need.id} className="pep-sol-rail__slide" data-sol-slide>
                  <NeedCard need={need} exploreLabel={copy.needsExplore} locale={locale} />
                </div>
              ))}
            </SolutionsScrollRail>
          </div>
        </div>
      </section>

      <section className="pep-sol-flagship" data-section="S-FLAGSHIP" aria-labelledby="solutions-flagship-h2">
        <div className="container">
          <PepApiErrorBanner error={error} onRetry={() => void reload()} />
          {loading && !error && !products ? <p className="text-secondary">{copy.loading}</p> : null}

          <div className="pep-sol-section-head">
            <h2 id="solutions-flagship-h2" className="pep-sol-section-title">
              {copy.flagshipTitle}
            </h2>
            <Link className="pep-sol-section-link" to="/products">
              {copy.flagshipAllProducts} →
            </Link>
          </div>

          <div className="pep-sol-flagship__desktop">
            <div className="pep-sol-phare-grid" data-tier="flagship">
              {flagship.map((editorial) => (
                <ProductShowcaseCard
                  key={editorial.slug}
                  editorial={editorial}
                  product={bySlug.get(editorial.slug)}
                  locale={locale}
                  badge={copy.badgeFlagship}
                  discoverLabel={copy.flagshipDiscover}
                />
              ))}
            </div>
            <p className="pep-sol-standard-eyebrow">{copy.standardEyebrow}</p>
            <div className="pep-sol-standard-grid" data-tier="standard">
              {standard.map((editorial) => (
                <ProductShowcaseCard
                  key={editorial.slug}
                  editorial={editorial}
                  product={bySlug.get(editorial.slug)}
                  locale={locale}
                  badge={copy.badgeStandard}
                  discoverLabel={copy.flagshipDiscover}
                />
              ))}
            </div>
          </div>

          <div className="pep-sol-flagship__mobile">
            <SolutionsScrollRail
              label={
                en
                  ? "Flagship solutions carousel. Use arrows or swipe."
                  : "Carrousel des solutions phares. Utilisez les flèches ou glissez."
              }
              prevLabel={copy.carouselPrev}
              nextLabel={copy.carouselNext}
            >
              {flagship.map((editorial) => (
                <div key={editorial.slug} className="pep-sol-rail__slide pep-sol-rail__slide--product" data-sol-slide>
                  <ProductShowcaseCard
                    editorial={editorial}
                    product={bySlug.get(editorial.slug)}
                    locale={locale}
                    badge={copy.badgeFlagship}
                    discoverLabel={copy.flagshipDiscover}
                  />
                </div>
              ))}
            </SolutionsScrollRail>
            <p className="pep-sol-standard-eyebrow">{copy.standardEyebrow}</p>
            <SolutionsScrollRail
              label={
                en
                  ? "Standard solutions carousel. Use arrows or swipe."
                  : "Carrousel des solutions standard. Utilisez les flèches ou glissez."
              }
              prevLabel={copy.carouselPrev}
              nextLabel={copy.carouselNext}
            >
              {standard.map((editorial) => (
                <div
                  key={editorial.slug}
                  className="pep-sol-rail__slide pep-sol-rail__slide--product pep-sol-rail__slide--standard"
                  data-sol-slide
                >
                  <ProductShowcaseCard
                    editorial={editorial}
                    product={bySlug.get(editorial.slug)}
                    locale={locale}
                    badge={copy.badgeStandard}
                    discoverLabel={copy.flagshipDiscover}
                  />
                </div>
              ))}
            </SolutionsScrollRail>
          </div>
        </div>
      </section>

      <section className="pep-sol-limited-wrap" data-section="S-LIMITED" aria-labelledby="solutions-limited-h2">
        <div className="container">
          <div className="pep-sol-limited-wrap__grid">
            <div>
              <h2 id="solutions-limited-h2" className="pep-sol-section-title">
                {copy.limitedTitle}
              </h2>
              <p className="pep-sol-section-lead">{copy.limitedLead}</p>
              <div className="pep-sol-limited__stack">
                {limited.map((editorial) => (
                  <LimitedCard
                    key={editorial.slug}
                    editorial={editorial}
                    product={bySlug.get(editorial.slug)}
                    locale={locale}
                    badge={copy.badgeLimited}
                    discoverLabel={copy.flagshipDiscover}
                  />
                ))}
              </div>
            </div>
            <aside className="pep-sol-platform" data-section="S-PLATFORM" aria-labelledby="solutions-platform-h2">
              <h2 id="solutions-platform-h2" className="pep-sol-platform__title">
                {copy.platformTitle}
              </h2>
              <p className="pep-sol-platform__lead">{copy.platformLead}</p>
              <PlatformDiagram center={copy.platformCenter} nodes={copy.platformNodes} />
              <Link className="pep-sol-platform__cta" to={copy.platformHref}>
                {copy.platformCta} →
              </Link>
            </aside>
          </div>
        </div>
      </section>

      <section
        className="pep-sol-journeys"
        id="solutions-journeys"
        data-section="S-JOURNEYS"
        aria-labelledby="solutions-journeys-h2"
      >
        <div className="container">
          <h2 id="solutions-journeys-h2" className="pep-sol-section-title">
            {copy.journeysAnchorLabel}
          </h2>
          <JourneysStrip locale={locale} />
        </div>
      </section>

      <section className="pep-sol-final" data-section="S-ACTION" aria-labelledby="solutions-final-h2">
        <div className="container pep-sol-final__inner">
          <div>
            <h2 id="solutions-final-h2" className="pep-sol-final__title">
              {copy.finalTitle}
            </h2>
            <p className="pep-sol-final__lead">{copy.finalLead}</p>
          </div>
          <div className="pep-sol-final__actions">
            <Link className="btn pep-sol-btn-primary" to="/configure">
              {en ? "Create my environment" : "Créer mon environnement"} →
            </Link>
            <Link className="btn pep-sol-btn-secondary" to="/contact?type=demo">
              {copy.finalDemo} →
            </Link>
            <Link className="btn pep-sol-btn-ghost" to="/contact">
              {copy.finalContact} →
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function JourneysStrip({ locale }: { locale: "fr" | "en" }) {
  const { data: solutions, loading, error, reload } = usePepFetch(getSolutions);
  const en = locale === "en";

  if (error) {
    return <PepApiErrorBanner error={error} onRetry={() => void reload()} />;
  }
  if (loading && !solutions) {
    return <p className="text-secondary">{en ? "Loading…" : "Chargement…"}</p>;
  }

  return (
    <ul className="pep-sol-journeys__list list-unstyled">
      {(solutions ?? []).map((s) => {
        const editorial = getJourneyEditorial(s.slug);
        const title = editorial
          ? en
            ? editorial.titleEn
            : editorial.titleFr
          : (s.displayNameFr ?? s.displayName);
        const blurb = editorial
          ? en
            ? editorial.blurbEn
            : editorial.blurbFr
          : (s.descriptionFr ?? s.description ?? "");
        return (
          <li key={s.slug}>
            <Link to={`/solutions/${s.slug}`}>
              <strong>{title}</strong>
              {blurb ? <span>{blurb}</span> : null}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/** Depth page — journeys; related products filtered to publishable catalog only. */
export function SolutionDetailPage() {
  const { slug = "" } = useParams();
  const { locale } = usePepI18n();
  const copy = solutionsCopy(locale);
  const en = locale === "en";
  const { data: solutions } = usePepFetch(getSolutions);
  const { data: products } = usePepFetch(getProducts);
  const editorial = getJourneyEditorial(slug);

  const solution = (solutions ?? []).find((s) => s.slug === slug) ?? null;
  const relatedSlugs = new Set(
    (editorial?.productSlugs ?? solution?.products ?? []).filter((s) =>
      SOLUTIONS_PUBLISHABLE_PRODUCT_SLUGS.has(s),
    ),
  );
  const related = (products ?? []).filter((p) => relatedSlugs.has(p.slug));

  if (!solution && !editorial) {
    return (
      <SiteLayout>
        <div className="container py-5">{copy.loading}</div>
      </SiteLayout>
    );
  }

  const name = editorial
    ? en
      ? editorial.titleEn
      : editorial.titleFr
    : (solution?.displayNameFr ?? solution?.displayName ?? slug);
  const long = editorial
    ? en
      ? editorial.longEn
      : editorial.longFr
    : (solution?.longDescriptionFr ?? solution?.descriptionFr ?? solution?.description ?? "");
  const benefits = editorial
    ? en
      ? editorial.benefitsEn
      : editorial.benefitsFr
    : (solution?.benefits ?? []);
  const seoTitle = editorial
    ? en
      ? editorial.seoTitleEn
      : editorial.seoTitleFr
    : en
      ? `Solution ${name} | ITaaS Suite`
      : `Solution ${name} | ITaaS Suite`;
  const seoDescription = editorial
    ? en
      ? editorial.seoDescriptionEn
      : editorial.seoDescriptionFr
    : long.slice(0, 160);
  const seoKeywords = editorial
    ? [...(en ? editorial.seoKeywordsEn : editorial.seoKeywordsFr)]
    : (solution?.seoKeywords ?? []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `https://www.itaas-suite.com/solutions/${slug}#webpage`,
        name: seoTitle,
        description: seoDescription,
        url: `https://www.itaas-suite.com/solutions/${slug}`,
        inLanguage: en ? "en" : "fr",
        isPartOf: { "@type": "WebSite", name: "ITaaS Suite", url: "https://www.itaas-suite.com/" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "ITaaS Suite",
            item: "https://www.itaas-suite.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: copy.breadcrumbSolutions,
            item: "https://www.itaas-suite.com/solutions",
          },
          {
            "@type": "ListItem",
            position: 3,
            name,
            item: `https://www.itaas-suite.com/solutions/${slug}`,
          },
        ],
      },
    ],
  };

  return (
    <SiteLayout>
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalPath={`/solutions/${slug}`}
        locale={locale}
        jsonLd={jsonLd}
      />
      <div className="pep-page-hero py-5 mb-0">
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb pep-breadcrumb mb-3">
              <li className="breadcrumb-item">
                <Link to="/solutions">{copy.breadcrumbSolutions}</Link>
              </li>
              <li className="breadcrumb-item active">{name}</li>
            </ol>
          </nav>
          <h1 className="display-5 fw-bold mb-3">{name}</h1>
          <p className="lead col-lg-9 mb-0 opacity-90">{long}</p>
        </div>
      </div>
      <div className="container py-5">
        {benefits.length > 0 && (
          <section className="mb-5">
            <h2 className="h4 fw-bold mb-3">{copy.detailBenefits}</h2>
            <div className="row g-3">
              {benefits.map((b) => (
                <div className="col-md-6" key={b}>
                  <div className="p-3 rounded-3 border h-100">
                    <span className="text-success me-2" aria-hidden="true">
                      ✓
                    </span>
                    {b}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
        {related.length > 0 && (
          <section>
            <h2 className="h4 fw-bold mb-3">{copy.detailProducts}</h2>
            <div className="row g-3">
              {related.map((p) => (
                <div className="col-md-4" key={p.slug}>
                  <ProductMiniCard product={p} />
                </div>
              ))}
            </div>
          </section>
        )}
        <div className="mt-5 text-center">
          <Link
            className="btn btn-primary btn-lg me-2"
            to={`/configure?package=${encodeURIComponent(slug)}`}
          >
            {en ? "Create my environment" : "Créer mon environnement"}
          </Link>
          <Link className="btn btn-outline-primary btn-lg me-2" to="/contact?type=demo">
            {copy.finalDemo}
          </Link>
          <Link className="btn btn-outline-secondary btn-lg" to="/contact">
            {copy.finalContact}
          </Link>
        </div>
      </div>
    </SiteLayout>
  );
}